<template>
  <div class="admin-calendarios">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h4 class="mb-25">Calendarios Laborales</h4>
        <small class="text-muted">Gestiona los calendarios de vacaciones por año</small>
      </div>
      <b-button variant="primary" @click="abrirModalNuevo">
        <feather-icon icon="PlusIcon" class="mr-50" />
        Nuevo Calendario
      </b-button>
    </div>

    <b-table
          :items="calendarios"
          :fields="campos"
          responsive
          show-empty
          empty-text="No hay calendarios"
        >
          <template #cell(activo)="data">
            <b-badge :variant="data.item.activo ? 'light-success' : 'light-secondary'">
              {{ data.item.activo ? 'Activo' : 'Inactivo' }}
            </b-badge>
          </template>

          <template #cell(acciones)="data">
            <div class="text-nowrap">
              <b-button
                variant="flat-primary"
                size="sm"
                class="btn-icon mr-50"
                @click="editarCalendario(data.item)"
                v-b-tooltip.hover
                title="Editar"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                variant="flat-warning"
                size="sm"
                class="btn-icon mr-50"
                @click="verFestivos(data.item)"
                v-b-tooltip.hover
                title="Gestionar Festivos"
              >
                <feather-icon icon="CalendarIcon" />
              </b-button>
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                @click="eliminar(data.item.id)"
                v-b-tooltip.hover
                title="Eliminar"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>

    <!-- Modal Crear/Editar -->
    <b-modal
      v-model="mostrarModal"
      :title="calendarioEditar ? 'Editar Calendario' : 'Nuevo Calendario'"
      ok-title="Guardar"
      cancel-title="Cancelar"
      centered
      @ok="guardar"
    >
      <b-form-group label="Nombre" label-for="nombre">
        <b-form-input v-model="formulario.nombre" placeholder="Ej: Calendario España 2025" />
      </b-form-group>

      <b-form-group label="Año" label-for="anio">
        <b-form-input v-model.number="formulario.anio" type="number" :disabled="!!calendarioEditar" />
      </b-form-group>

      <b-form-group label="Dias de vacaciones base" label-for="dias">
        <b-form-input v-model.number="formulario.dias_vacaciones_base" type="number" />
      </b-form-group>

      <b-form-group v-if="calendarioEditar" label="Estado">
        <b-form-checkbox v-model="formulario.activo" switch>
          {{ formulario.activo ? 'Activo' : 'Inactivo' }}
        </b-form-checkbox>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable, BBadge, BButton, BModal, BFormGroup, BFormInput, BFormCheckbox,
  VBTooltip
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BTable,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      mostrarModal: false,
      calendarioEditar: null,
      formulario: {
        nombre: '',
        anio: new Date().getFullYear(),
        dias_vacaciones_base: 22,
        activo: true,
      },
      campos: [
        { key: 'nombre', label: 'Nombre' },
        { key: 'anio', label: 'Año', sortable: true, class: 'text-center' },
        { key: 'dias_vacaciones_base', label: 'Dias Base', class: 'text-center' },
        { key: 'total_festivos', label: 'Festivos', class: 'text-center' },
        { key: 'total_usuarios', label: 'Usuarios', class: 'text-center' },
        { key: 'activo', label: 'Estado', class: 'text-center' },
        { key: 'acciones', label: 'Acciones', class: 'text-center', thStyle: { width: '150px' } },
      ],
    }
  },

  computed: {
    ...mapState('vacaciones', ['calendarios']),
  },

  mounted() {
    this.fetchCalendarios()
  },

  methods: {
    ...mapActions('vacaciones', ['fetchCalendarios', 'crearCalendario', 'actualizarCalendario', 'eliminarCalendario']),

    abrirModalNuevo() {
      this.calendarioEditar = null
      this.formulario = {
        nombre: '',
        anio: new Date().getFullYear(),
        dias_vacaciones_base: 22,
        activo: true,
      }
      this.mostrarModal = true
    },

    editarCalendario(calendario) {
      this.calendarioEditar = calendario
      this.formulario = { ...calendario }
      this.mostrarModal = true
    },

    verFestivos(calendario) {
      this.$emit('ver-festivos', calendario)
    },

    async guardar(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.formulario.nombre || !this.formulario.anio) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Nombre y año son requeridos', icon: 'AlertTriangleIcon', variant: 'warning' },
        })
        return
      }

      try {
        if (this.calendarioEditar) {
          await this.actualizarCalendario({ id: this.calendarioEditar.id, ...this.formulario })
        } else {
          await this.crearCalendario(this.formulario)
        }
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Calendario guardado', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModal = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: error.error || 'Error al guardar', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    async eliminar(id) {
      this.$bvModal.msgBoxConfirm('¿Eliminar este calendario? Se eliminaran tambien sus festivos y asignaciones.', {
        title: 'Confirmar eliminacion',
        okVariant: 'danger',
        okTitle: 'Si, eliminar',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await this.eliminarCalendario(id)
            this.$toast({
              component: ToastificationContent,
              props: { title: 'Calendario eliminado', icon: 'CheckIcon', variant: 'success' },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: { title: error.error || 'Error al eliminar', icon: 'AlertTriangleIcon', variant: 'danger' },
            })
          }
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-calendarios {
  .btn-icon {
    padding: 0.5rem;

    &:hover {
      transform: scale(1.1);
    }
  }
}
</style>
